/**
 * useAsync — الجسر بين Provider وبين حالات الواجهة الثمانية.
 *
 * الواجهة لا تتعامل مع try/catch ولا مع أكواد الأخطاء؛ تستقبل حالة واحدة
 * واضحة: idle | loading | success | empty | no-data | error.
 *
 * ملاحظة مهمة: `no-data` ليست خطأً، و`low-confidence` ليست خطأً — الأخيرة
 * حالة داخل النتيجة الناجحة وتُقرأ من `confidence_score`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ProviderError, type ProviderErrorCode } from '@/domain/market-data-provider';

export type AsyncStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'empty'
  | 'no-data'
  /** الميزة نفسها غير مطلقة بعد — «قيد الإطلاق»، وليست خطأً. */
  | 'unavailable'
  | 'error';

export interface AsyncState<T> {
  status: AsyncStatus;
  data: T | null;
  error: {
    code: ProviderErrorCode | 'unknown';
    message: string;
    /** تشخيص الرفض بالأرقام الفعلية — يُعرض بدل نص ثابت. */
    rejection?: { headline: string; facts: string[]; suggestion: string | null } | null;
  } | null;
  reload: () => void;
}

export interface UseAsyncOptions<T> {
  /** يحدد ما إذا كانت النتيجة الناجحة فارغة (Empty State). */
  isEmpty?: (data: T) => boolean;
  /** تعطيل التنفيذ التلقائي. */
  enabled?: boolean;
}

export function useAsync<T>(
  task: () => Promise<T>,
  deps: readonly unknown[],
  options: UseAsyncOptions<T> = {},
): AsyncState<T> {
  const { isEmpty, enabled = true } = options;
  const [state, setState] = useState<{
    status: AsyncStatus;
    data: T | null;
    error: AsyncState<T>['error'];
  }>({ status: enabled ? 'loading' : 'idle', data: null, error: null });
  const [nonce, setNonce] = useState(0);
  const taskRef = useRef(task);
  taskRef.current = task;

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle', data: null, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ status: 'loading', data: s.data, error: null }));

    taskRef
      .current()
      .then((data) => {
        if (cancelled) return;
        const empty = isEmpty ? isEmpty(data) : false;
        setState({ status: empty ? 'empty' : 'success', data, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ProviderError) {
          setState({
            status:
              err.code === 'insufficient_data'
                ? 'no-data'
                : err.code === 'feature_unavailable'
                  ? 'unavailable'
                  : 'error',
            data: null,
            error: { code: err.code, message: err.message, rejection: err.rejection ?? null },
          });
          return;
        }
        setState({
          status: 'error',
          data: null,
          error: { code: 'unknown', message: 'حدث خطأ غير متوقع.' },
        });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { status: state.status, data: state.data, error: state.error, reload };
}
